import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Table, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { dayOffApplicationsApiService } from '../../services/apiServices';
import { DayOffApplicationResponse, DayOffApplicationType } from '../../api-client';
import {
  getDayOffApplicationStatusLabel,
  getDayOffApplicationStatusBadgeVariant,
  getDayOffApplicationTypeLabel,
} from '../utils/statusConverters';
import { SmartPagination } from '../../components/SmartPagination';

const DayOffApplicationsListPage: React.FC = () => {
  const [applications, setApplications] = useState<DayOffApplicationResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [userSearchInput, setUserSearchInput] = useState<string>('');

  const userSearchFilter = searchParams.get('user_search') || '';
  const statusFilter = searchParams.get('status') || '';
  const typeFilter = searchParams.get('type') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  useEffect(() => {
    setUserSearchInput(userSearchFilter);
  }, [userSearchFilter]);

  const requestParamsRef = useRef<string | null>(null);
  const isRequestInProgressRef = useRef<boolean>(false);

  useEffect(() => {
    const paramsString = JSON.stringify({
      user_search: userSearchFilter,
      status: statusFilter,
      type: typeFilter,
      page: currentPage,
    });

    if (requestParamsRef.current === paramsString) {
      return;
    }

    if (isRequestInProgressRef.current) {
      return;
    }

    requestParamsRef.current = paramsString;
    isRequestInProgressRef.current = true;
    setLoading(true);
    setError(null);

    const loadApplications = async () => {
      try {
        const response = await dayOffApplicationsApiService.getApplications(
          limit,
          (currentPage - 1) * limit,
          statusFilter || undefined,
          undefined,
          userSearchFilter || undefined,
          typeFilter || undefined
        );

        setApplications(response.data || []);
      } catch (err: any) {
        console.error('Ошибка при загрузке заявок на выходной:', err);
        setError(err.message || 'Ошибка при загрузке заявок');
      } finally {
        isRequestInProgressRef.current = false;
        setLoading(false);
      }
    };

    loadApplications();

    return () => {
      isRequestInProgressRef.current = false;
    };
  }, [userSearchFilter, statusFilter, currentPage, limit, typeFilter]);

  const updateUrlParams = useCallback(
    (newParams: Partial<Record<string, string>>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          Object.entries(newParams).forEach(([key, value]) => {
            if (value === '' || value === null || value === undefined) {
              params.delete(key);
            } else {
              params.set(key, value);
            }
          });

          if (!('page' in newParams)) {
            params.set('page', '1');
          }

          return params;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const handleUserSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        updateUrlParams({ user_search: userSearchInput, page: '1' });
      }
    },
    [userSearchInput, updateUrlParams]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      updateUrlParams({ status: value, page: '1' });
    },
    [updateUrlParams]
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      updateUrlParams({ type: value, page: '1' });
    },
    [updateUrlParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('page', newPage.toString());
        return params;
      });
    },
    [setSearchParams]
  );

  const handleResetFilters = useCallback(() => {
    setUserSearchInput('');
    updateUrlParams({ user_search: '', status: '', type: '', page: '1' });
  }, [updateUrlParams]);

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявки на выходной (Day Off)</h2>
          </div>

          <div className="row mb-4">
            <div className="col-md-3 mb-2">
              <Form.Label>Фильтр по пользователю (email, имя пользователя или ФИО)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Email, имя пользователя или ФИО"
                value={userSearchInput}
                onChange={(e) => setUserSearchInput(e.target.value)}
                onKeyDown={handleUserSearchKeyDown}
              />
            </div>
            <div className="col-md-3 mb-2">
              <Form.Label>Статус</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="">Все</option>
                <option value="pending">Ожидает рассмотрения</option>
                <option value="active">Активна</option>
                <option value="completed">Завершена</option>
                <option value="canceled">Отменено</option>
              </Form.Select>
            </div>
            <div className="col-md-3 mb-2">
              <Form.Label>Тип выходного</Form.Label>
              <Form.Select value={typeFilter} onChange={(e) => handleTypeChange(e.target.value)}>
                <option value="">Все</option>
                <option value="paid">Оплачиваемый</option>
                <option value="unpaid">Без сохранения зарплаты</option>
              </Form.Select>
            </div>
            <div className="col-md-2 mb-2 d-flex align-items-end">
              <Button variant="outline-secondary" className="w-100" onClick={handleResetFilters}>
                Сбросить
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Загрузка заявок...</p>
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Пользователь</th>
                    <th>Причина</th>
                    <th>Тип</th>
                    <th>Статус</th>
                    <th>Дата создания</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {applications && applications.length > 0 ? (
                    applications.map((app) => {
                      const userEmail = app.user?.email || '—';
                      const userName = app.user?.username || '—';
                      const userFullName = app.user?.profile?.full_name;
                      return (
                        <tr key={app.id}>
                          <td>{app.id}</td>
                          <td>
                            {userFullName && (
                              <div>
                                <strong>{userFullName}</strong>
                              </div>
                            )}
                            <div>
                              <strong>{userName}</strong>
                            </div>
                            <small className="text-muted">{userEmail}</small>
                          </td>
                          <td>{app.reason || '—'}</td>
                          <td>
                            <Badge
                              bg={app.type === DayOffApplicationType.PAID ? 'info' : 'warning'}
                            >
                              {getDayOffApplicationTypeLabel(app.type)}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={getDayOffApplicationStatusBadgeVariant(app.status)}>
                              {getDayOffApplicationStatusLabel(app.status)}
                            </Badge>
                          </td>
                          <td>{new Date(app.created_at).toLocaleDateString()}</td>
                          <td>
                            <Link to={`/vacation/day-off/${app.id}`}>
                              <Button variant="outline-primary" size="sm">
                                Подробнее
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        Заявки не найдены
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              <SmartPagination
                currentPage={currentPage}
                limit={limit}
                currentItemsCount={applications.length}
                onPageChange={handlePageChange}
                siblingCount={1}
              />
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default DayOffApplicationsListPage;
